import { useRef, useState } from 'react'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import { HoverHelp } from '@/tableControls'
import { solveMaxDistribution } from '../engine/cashPolicySolver'
import { evaluate } from '../engine'
import type { CaseInputs, CashPolicy, EvaluateContext, History, SchemaEntry } from '../engine/types'
import { monthLabel } from '../monthColumns'
import { formatCurrency, formatCurrencyWhole } from '../projectionsFormat'
import SegmentedToggle from './SegmentedToggle'
import { POLICY_CONTROL_WIDTH } from './policyLayout'

type SolverBlockProps = {
  policy: CashPolicy
  draft: CaseInputs
  schema: SchemaEntry[]
  history: History
  lastClosedMonth: string
  context: EvaluateContext
  solverUpper: number
  disabled?: boolean
  disabledTooltip?: string
  onPolicyChange: (patch: Partial<CashPolicy>) => void
  onSolverChange: (patch: Partial<CashPolicy['solver']>) => void
}

type SolverResult = {
  amount: number
  rounded_down_to_step: number
  binding_month_t: number | null
  min_operating_cash_at_amount: number
}

export default function SolverBlock({
  policy,
  draft,
  schema,
  history,
  lastClosedMonth,
  context,
  solverUpper,
  disabled = false,
  disabledTooltip,
  onPolicyChange,
  onSolverChange,
}: SolverBlockProps) {
  const [solving, setSolving] = useState(false)
  const [result, setResult] = useState<SolverResult | null>(null)
  const lastSolveAt = useRef(0)

  const runSolver = async () => {
    const now = Date.now()
    if (now - lastSolveAt.current < 150) {
      return
    }
    lastSolveAt.current = now
    setSolving(true)
    try {
      const inputs: CaseInputs = {
        ...structuredClone(draft),
        cash_policy: {
          ...structuredClone(policy),
          distribution: {
            ...policy.distribution,
            split_schedule: null,
          },
        },
      }
      const solved = solveMaxDistribution({
        evaluate,
        schema,
        history,
        inputs,
        context,
        horizonMonths: policy.solver.horizon_months,
        step: policy.solver.round_to,
        upper: Math.floor(solverUpper / policy.solver.round_to) * policy.solver.round_to,
        lastClosedMonth,
      })
      setResult({ ...solved, rounded_down_to_step: solved.amount })
    } finally {
      setSolving(false)
    }
  }

  const bindingLabel =
    result?.binding_month_t != null
      ? monthLabel(lastClosedMonth, result.binding_month_t)
      : null

  const solveButton = (
    <Button
      variant="primary"
      gradient
      size="sm"
      className="w-full"
      disabled={solving || disabled}
      onClick={() => void runSolver()}
    >
      {solving ? 'Solving…' : 'Solve for max sustainable'}
    </Button>
  )

  return (
    <div className="mt-2 space-y-2">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs text-gray-600">
          <HoverHelp label="Horizon" aria-label="Solver horizon help">
            How many projected months to search when solving for the maximum sustainable
            distribution amount.
          </HoverHelp>
        </span>
        <div className={`shrink-0 ${POLICY_CONTROL_WIDTH}`}>
          <SegmentedToggle<12 | 24 | 36>
            fullWidth
            value={policy.solver.horizon_months}
            options={[
              { value: 12, label: '12M' },
              { value: 24, label: '24M' },
              { value: 36, label: '36M' },
            ]}
            onChange={(horizon_months) => onSolverChange({ horizon_months })}
            ariaLabel="Solver horizon months"
          />
        </div>
      </div>
      {disabled && disabledTooltip ? (
        <HoverHelp label={solveButton} aria-label="Solve for max sustainable help">
          {disabledTooltip}
        </HoverHelp>
      ) : (
        <HoverHelp label={solveButton} aria-label="Solve for max sustainable help">
          Finds the max distribution with surplus left in operating cash (sweep ignored). Cut
          the amount back if you want residual swept to the money market.
        </HoverHelp>
      )}
      {result ? (
        <div className="text-xs text-gray-700">
          <span>
            Max {formatCurrencyWhole(result.rounded_down_to_step)} (assumes no sweep)
            {bindingLabel
              ? ` · limited by ${bindingLabel} (min cash ${formatCurrency(result.min_operating_cash_at_amount)})`
              : ''}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="ml-1 h-6 px-2 text-xs"
            onClick={() =>
              onPolicyChange({
                distribution: {
                  ...policy.distribution,
                  amount_per_period: result.rounded_down_to_step,
                },
              })
            }
          >
            Use
          </Button>
        </div>
      ) : null}
    </div>
  )
}
